import * as React from 'react';

import { cn } from '@workspace/ui/lib/utils';

import { Step } from '~/components/generator/shared/step';
import { GeneratorStep } from '~/schemas/generator/complete-generator-schema';

export type StepIndicatorProps = React.HtmlHTMLAttributes<HTMLDivElement> & {
  steps: GeneratorStep[];
  currentStep: GeneratorStep;
  setCurrentStep: (value: GeneratorStep) => void;
  furthestCompletedStepIndex?: number;
  validateStep?: (step: GeneratorStep) => boolean;
};

export function StepIndicator({
  steps,
  currentStep,
  setCurrentStep,
  furthestCompletedStepIndex,
  validateStep,
  className,
  ...other
}: StepIndicatorProps): React.JSX.Element {
  const currentStepIndex = steps.findIndex((step) => step === currentStep);
  return (
    <div
      className={cn('flex flex-row gap-4 mb-4', className)}
      {...other}
    >
      {steps.map((step) => {
        const stepIndex = steps.findIndex((s) => s === step);
        const maxEnabledIndex =
          typeof furthestCompletedStepIndex === 'number'
            ? furthestCompletedStepIndex
            : currentStepIndex;

        // Check if we can navigate to this step
        // We can navigate to a step if:
        // 1. It's the current step (always allowed)
        // 2. It's a previous step (always allowed)
        // 3. It's a future step AND either:
        //    - All steps from current to target are valid, OR
        //    - It's the first invalid step
        const canNavigateToStep = (targetIndex: number): boolean => {
          if (targetIndex === currentStepIndex) return true; // Current step
          if (targetIndex < currentStepIndex) return true; // Previous step

          // For future steps
          if (!validateStep) return targetIndex <= maxEnabledIndex;

          // Check if this is the first invalid step
          let isFirstInvalidStep = false;
          for (let i = currentStepIndex; i <= targetIndex; i++) {
            if (!validateStep(steps[i])) {
              isFirstInvalidStep = i === targetIndex;
              break;
            }
          }

          // Allow navigation if it's the first invalid step
          if (isFirstInvalidStep) return true;

          // Otherwise, check if all steps from current to target are valid
          for (let i = currentStepIndex; i <= targetIndex; i++) {
            if (!validateStep(steps[i])) {
              return false;
            }
          }
          return true;
        };

        const active = stepIndex === currentStepIndex;
        const disabled =
          !canNavigateToStep(stepIndex) || currentStepIndex === stepIndex;
        // Don't highlight the review step when it's not current (it's always valid but shouldn't be highlighted)
        const isValid =
          validateStep && step !== 'review' ? validateStep(step) : false;
        return (
          <Step
            key={stepIndex}
            step={step}
            active={active}
            disabled={disabled}
            isValid={isValid}
            setCurrentStep={setCurrentStep}
          />
        );
      })}
    </div>
  );
}
